import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';
import { groupPlaces, type RatedLibraryPlace } from '../../lib/placeGroups';
import { AdminPage } from './AdminPage';

function Stars({ avg, count }: { avg: number; count: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-app-fg whitespace-nowrap">
      <i className="ti ti-star-filled" style={{ color: 'var(--accent)', fontSize: 13 }} />
      {avg.toFixed(1)} <span className="text-app-muted">({count})</span>
    </span>
  );
}

export function AdminPlaceRatings() {
  const [places, setPlaces] = useState<RatedLibraryPlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<number>>(new Set());

  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase.rpc('admin_place_rating_stats');
      if (err) setError(err.message);
      setPlaces(((data ?? []) as RatedLibraryPlace[]).map((p) => ({
        ...p, avg_rating: Number(p.avg_rating), rating_count: Number(p.rating_count),
      })));
      setLoading(false);
    })();
  }, []);

  const groups = useMemo(() => groupPlaces(places), [places]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? groups.filter((g) => g.name.toLowerCase().includes(q) || (g.address ?? '').toLowerCase().includes(q)) : groups;
  }, [groups, search]);

  function toggle(i: number) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <AdminPage
      title="Place ratings"
      description="What travelers think of each place. The same place registered by several hosts is combined (same name, within ~100 m); expand a row to see each host's own numbers."
    >
      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <>
          {error && <p className="text-sm text-crimson mb-4">{error}</p>}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search places"
            className="w-full md:w-72 mb-4 px-3 py-2 text-sm rounded-lg bg-app-card border border-app-border text-app-fg"
          />
          {shown.length === 0 ? (
            <p className="text-sm text-app-muted py-10 text-center">No ratings yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {shown.map((g, i) => (
                <div key={g.entries[0].library_item_id} className="bg-app-card border border-app-border rounded-xl">
                  <button type="button" onClick={() => toggle(i)} className="w-full flex items-center gap-4 px-4 py-3 text-left">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-app-fg truncate">{g.name}</div>
                      {g.address && <div className="text-xs text-app-muted truncate">{g.address}</div>}
                    </div>
                    <span className="text-xs text-app-muted whitespace-nowrap">
                      {g.entries.length} host{g.entries.length === 1 ? '' : 's'}
                    </span>
                    <Stars avg={g.avg} count={g.count} />
                    <i className={`ti ti-chevron-${open.has(i) ? 'up' : 'down'} text-app-muted`} />
                  </button>
                  {open.has(i) && (
                    <div className="border-t border-app-border px-4 py-2 flex flex-col gap-1">
                      {g.entries.map((e) => (
                        <div key={e.library_item_id} className="flex items-center justify-between gap-4 text-xs py-1">
                          <span className="text-app-muted truncate">{e.host_name ?? 'Unknown host'}</span>
                          <Stars avg={e.avg_rating} count={e.rating_count} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}

export default AdminPlaceRatings;
